import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { ShareCard } from './Waitlist';

/**
 * Home — the Visionry Go landing tab.
 *
 * Sprint 1B additions:
 *  - "Offer a lesson slot" — the money feature. Instructor picks date/time/
 *    audience; the API blasts the offer, first claim wins. Roster claims
 *    confirm instantly; waitlist claims come back here (and to WhatsApp) as
 *    Approve/Decline.
 *  - Live slots list — every open/pending offer with its state, so the
 *    dashboard mirrors what WhatsApp is doing.
 *  - "€ recovered" — placements + confirmed fills × lesson price. Value that
 *    isn't displayed doesn't exist in the customer's head.
 */

const API_BASE  = 'https://visionry-api-production.up.railway.app';

const C = {
  bg: '#061618', card: '#0d3338', card2: '#10434a', border: '#10434a',
  teal: '#25a2b3', tealSoft: '#5fc4d2', text: '#e0f0f2', muted: '#7fb8c0',
  faint: '#3d5a5f', green: '#00cc88', greenBg: '#06231a',
  red: '#ff6b6b', redBg: '#2a0f0f', amber: '#ffb84d'
};

const MOBILE_MAX = 640;
function useIsMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' ? window.innerWidth <= MOBILE_MAX : false);
  useEffect(() => {
    const onR = () => setM(window.innerWidth <= MOBILE_MAX);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);
  return m;
}

function waitedLabel(iso) {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'joined today';
  if (days === 1) return 'waiting 1 day';
  if (days < 7) return `waiting ${days} days`;
  const weeks = Math.floor(days / 7);
  return weeks === 1 ? 'waiting 1 week' : `waiting ${weeks} weeks`;
}

function slotWhen(iso) {
  return new Date(iso).toLocaleString('en-GB', {
    timeZone: 'Europe/Dublin', weekday: 'short', day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit', hour12: false
  });
}

async function apiPost(path, body) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
    body: JSON.stringify(body)
  });
  const json = await res.json().catch(() => ({}));
  return { ok: res.ok, ...json };
}

function StatCard({ label, value, hint }) {
  return (
    <div style={{
      flex: 1, minWidth: 0, background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 14, padding: '14px 12px', textAlign: 'center'
    }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: C.tealSoft }}>{value}</div>
      <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>{label}</div>
      {hint && <div style={{ fontSize: 10, color: C.faint, marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

// ── Offer a lesson slot ──
// Pre-fill the slot form with "now, next round hour" in IRISH time — a
// cancellation offer is usually for today; typing today's date every time is
// friction at the exact moment the instructor is busiest. sv-SE locale gives
// ISO yyyy-mm-dd / HH:mm strings directly.
function dublinNowPrefill() {
  const next = new Date(Date.now() + 60 * 60000);
  const date = next.toLocaleDateString('sv-SE', { timeZone: 'Europe/Dublin' });
  const hh   = next.toLocaleTimeString('sv-SE', { timeZone: 'Europe/Dublin', hour: '2-digit' }).slice(0, 2);
  return { date, time: `${hh}:00` };
}

const LENGTH_PRESETS = [60, 90, 120];
const lengthLabel = mins => mins % 60 === 0 ? `${mins / 60} hr` : `${mins} min`;

// Weekday-safe date label for the review step — parses the date portion at
// noon UTC to dodge local-browser-timezone midnight boundary issues (date/
// time here are Dublin wall-clock strings the instructor typed; the time
// portion is shown exactly as entered, no conversion needed for display).
function reviewDateTimeLabel(dateStr, timeStr) {
  if (!dateStr || !timeStr) return '';
  const d = new Date(`${dateStr}T12:00:00Z`);
  const dayLabel = isNaN(d.getTime())
    ? dateStr
    : d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
  return `${dayLabel} at ${timeStr}`;
}

const AUDIENCE_REVIEW_LABEL = {
  roster: 'your students', both: 'your students and waitlist', waitlist: 'your waitlist'
};

function OfferSlotCard({ rosterCount, locations, standardMinutes, onSent, isMobile, isFillMode }) {
  const prefill = dublinNowPrefill();
  const [open, setOpen]         = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [date, setDate]         = useState(prefill.date);
  const [time, setTime]         = useState(prefill.time);
  const [location, setLocation] = useState('');
  const [area, setArea]         = useState('');
  const [duration, setDuration] = useState(standardMinutes || 60);
  // Fill has no waitlist product — every offer goes to students only.
  const [audience, setAudience] = useState(isFillMode ? 'roster' : (rosterCount > 0 ? 'both' : 'waitlist'));
  const [busy, setBusy]         = useState(false);
  const [note, setNote]         = useState(null);

  // Area is informational only (shown up front in the offer message) — never
  // a filter, every eligible student still gets every offer regardless of
  // their own area. Only worth showing the picker once there's an actual
  // choice to make — and only worth its own step for the same reason.
  const showAreaPicker = (locations || []).length >= 2;

  // Fill offers to students only — no waitlist audience option at all.
  const AUDIENCES = isFillMode
    ? [{ id: 'roster', label: `My students (${rosterCount})`, disabled: false }]
    : [
        { id: 'roster',   label: `My students (${rosterCount})`, disabled: rosterCount === 0 },
        { id: 'both',     label: 'Both',                          disabled: rosterCount === 0 },
        { id: 'waitlist', label: 'Waitlist',                      disabled: false }
      ];

  // Two steps: fill everything in one go, then review before it fires.
  const STEPS = ['form', 'review'];
  const currentStep = STEPS[stepIndex];
  const canProceed = currentStep !== 'form' || !!(date && time);

  function toggleOpen() {
    // Same reset the card has always done on any header-button tap (note
    // cleared regardless of direction) — step index resets alongside it, so
    // closing from any step and reopening later always starts at step 1.
    setOpen(o => !o);
    setNote(null);
    setStepIndex(0);
  }
  function next() { if (canProceed) setStepIndex(i => Math.min(i + 1, STEPS.length - 1)); }
  function back() { setStepIndex(i => Math.max(i - 1, 0)); }

  async function submit() {
    if (!date || !time) { setNote({ type: 'err', text: 'Pick a date and time.' }); return; }
    setBusy(true); setNote(null);
    const res = await apiPost('/open-slot', {
      date, time, location: location.trim() || null, audience,
      area: area || null, duration_minutes: duration || null
    });
    if (res.ok) {
      setNote({ type: 'ok', text: `Offer sent to ${res.sent} ${res.sent === 1 ? 'person' : 'people'} ✅ First to claim gets it.` });
      const p = dublinNowPrefill(); setDate(p.date); setTime(p.time); setLocation(''); setArea(''); setDuration(standardMinutes || 60);
      setStepIndex(0);
      onSent && onSent();
    } else {
      setNote({ type: 'err', text: res.error || 'Could not send the offer.' });
    }
    setBusy(false);
  }

  const input = {
    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10,
    color: C.text, fontSize: 14, padding: '10px 12px', minHeight: 44,
    fontFamily: 'Poppins, sans-serif', width: '100%', boxSizing: 'border-box',
    colorScheme: 'dark'
  };
  const navBtn = (primary, disabled) => ({
    flex: isMobile ? 1 : 'none',
    background: primary ? C.teal : 'none',
    border: primary ? 'none' : `1px solid ${C.border}`,
    borderRadius: 10, color: primary ? C.bg : C.muted,
    fontSize: 14, fontWeight: 600, padding: '11px 20px', minHeight: 44,
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
    minWidth: isMobile ? 0 : 100
  });

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Offer a lesson slot</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
            Cancellation or a free hour? Offer it out — first to claim gets it.
          </div>
        </div>
        <button onClick={toggleOpen} style={{
          background: open ? C.card2 : C.teal, border: 'none', borderRadius: 10,
          color: open ? C.text : C.bg, fontSize: 14, fontWeight: 600,
          padding: '10px 16px', minHeight: 44, cursor: 'pointer', whiteSpace: 'nowrap'
        }}>
          {open ? 'Close' : '＋ Offer slot'}
        </button>
      </div>

      {open && (
        <div style={{ marginTop: 14 }}>
          {/* Progress dots */}
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 16 }}>
            {STEPS.map((id, i) => (
              <div key={id} style={{
                width: 7, height: 7, borderRadius: '50%',
                background: i === stepIndex ? C.teal : C.border
              }} />
            ))}
          </div>

          {currentStep === 'form' && (
            <>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>When's the slot?</div>
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8, marginBottom: 8 }}>
                <input type="date" value={date} onChange={e => setDate(e.target.value)} style={input} />
                <input type="time" value={time} onChange={e => setTime(e.target.value)} style={input} />
              </div>
              <input type="text" placeholder="Pickup location (optional)" value={location}
                onChange={e => setLocation(e.target.value)} style={{ ...input, marginBottom: 14 }} />

              <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>How long?</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                {LENGTH_PRESETS.map(mins => (
                  <button key={mins} onClick={() => setDuration(mins)} style={{
                    flex: isMobile ? 1 : 'none',
                    background: duration === mins ? C.card2 : 'none',
                    border: `1px solid ${duration === mins ? C.teal : C.border}`,
                    borderRadius: 10, color: duration === mins ? C.tealSoft : C.muted,
                    fontSize: 13, fontWeight: 600, padding: '9px 14px', minHeight: 44,
                    cursor: 'pointer'
                  }}>
                    {lengthLabel(mins)}
                  </button>
                ))}
              </div>

              {showAreaPicker && (
                <>
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Which area?</div>
                  <select value={area} onChange={e => setArea(e.target.value)} style={{ ...input, marginBottom: 14 }}>
                    <option value="">No specific area</option>
                    {locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                  </select>
                </>
              )}

              {isFillMode ? (
                <div style={{ fontSize: 12, color: C.muted }}>
                  Offered to: <span style={{ color: C.tealSoft, fontWeight: 600 }}>your students ({rosterCount})</span>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Send the offer to:</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {AUDIENCES.map(a => (
                      <button key={a.id} onClick={() => !a.disabled && setAudience(a.id)} disabled={a.disabled} style={{
                        flex: isMobile ? 1 : 'none', minWidth: isMobile ? 100 : 'auto',
                        background: audience === a.id ? C.card2 : 'none',
                        border: `1px solid ${audience === a.id ? C.teal : C.border}`,
                        borderRadius: 10, color: a.disabled ? C.faint : (audience === a.id ? C.tealSoft : C.muted),
                        fontSize: 13, fontWeight: 600, padding: '9px 14px', minHeight: 44,
                        cursor: a.disabled ? 'not-allowed' : 'pointer'
                      }}>
                        {a.label}
                      </button>
                    ))}
                  </div>
                  {audience === 'both' && (
                    <div style={{ fontSize: 11.5, color: C.faint, marginTop: 10 }}>
                      Your students get first refusal — the waitlist is offered it if unclaimed after 2 hours.
                    </div>
                  )}
                  {rosterCount === 0 && (
                    <div style={{ fontSize: 11.5, color: C.faint, marginTop: 10 }}>
                      Add your current students in Settings to offer slots to them too.
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {currentStep === 'review' && (
            <>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Review & send</div>
              <div style={{
                background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10,
                padding: 14, fontSize: 14, color: C.text, lineHeight: 1.5
              }}>
                Offering a {lengthLabel(duration)} slot on {reviewDateTimeLabel(date, time)}
                {area ? ` in ${area}` : ''}, to {AUDIENCE_REVIEW_LABEL[audience] || audience}.
                {location.trim() && (
                  <div style={{ fontSize: 12.5, color: C.muted, marginTop: 6 }}>📍 Pickup: {location.trim()}</div>
                )}
              </div>
            </>
          )}

          {note && (
            <div style={{
              marginTop: 12, padding: '9px 12px', borderRadius: 8, fontSize: 13,
              background: note.type === 'ok' ? C.greenBg : C.redBg,
              color:      note.type === 'ok' ? C.green : C.red
            }}>
              {note.text}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            {stepIndex > 0 && (
              <button onClick={back} style={navBtn(false, false)}>Back</button>
            )}
            {currentStep === 'review' ? (
              <button onClick={submit} disabled={busy} style={navBtn(true, busy)}>
                {busy ? 'Sending…' : 'Send offer'}
              </button>
            ) : (
              <button onClick={next} disabled={!canProceed} style={navBtn(true, !canProceed)}>
                Next
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Live slots (open + pending claims) ──
function SlotChip({ status, deposit }) {
  const map = {
    open:    { bg: C.card2,   fg: C.tealSoft, label: 'Waiting for claims' },
    claimed: { bg: C.amber,   fg: '#2a1f0a',  label: 'Needs your decision' }
  };
  let m = map[status] || { bg: C.card2, fg: C.muted, label: status };
  if (status === 'confirmed' && deposit === 'pending') m = { bg: C.card2, fg: C.amber, label: 'Awaiting deposit' };
  if (status === 'confirmed' && deposit === 'sent')    m = { bg: C.green, fg: C.greenBg, label: 'Deposit sent — confirm?' };
  return (
    <span style={{ background: m.bg, color: m.fg, borderRadius: 6, fontSize: 11, fontWeight: 700, padding: '3px 8px', whiteSpace: 'nowrap' }}>
      {m.label}
    </span>
  );
}

function ActiveSlots({ slots, busy, onDecide, onDeposit }) {
  if (!slots.length) return null;
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '4px 16px', marginBottom: 14 }}>
      {slots.map(s => (
        <div key={s.id} style={{ padding: '12px 0', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text, flex: 1, minWidth: 140 }}>
              {slotWhen(s.slot_start_iso)}{s.location ? ` · ${s.location}` : ''}
            </div>
            <SlotChip status={s.status} deposit={s.deposit_status} />
          </div>
          {s.status === 'confirmed' && ['pending', 'sent'].includes(s.deposit_status || '') && (
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 13, color: C.muted, flex: 1, minWidth: 120 }}>
                <span style={{ color: C.text, fontWeight: 600 }}>{s.claimed_name || 'The student'}</span>
                {s.deposit_status === 'sent' ? ' says the deposit is sent — check your account.' : ' has been asked for the deposit.'}
              </div>
              <button disabled={busy === s.id} onClick={() => onDeposit(s)} style={{
                background: C.green, border: 'none', borderRadius: 10, color: C.greenBg,
                fontSize: 13, fontWeight: 700, padding: '9px 14px', minHeight: 40, cursor: 'pointer'
              }}>
                {busy === s.id ? '…' : 'Deposit received ✓'}
              </button>
            </div>
          )}
          {s.status === 'claimed' && (
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 13, color: C.muted, flex: 1, minWidth: 120 }}>
                Claimed by <span style={{ color: C.text, fontWeight: 600 }}>{s.claimed_name || 'a waitlisted student'}</span>
              </div>
              <button disabled={busy === s.id} onClick={() => onDecide(s, 'approve')} style={{
                background: C.green, border: 'none', borderRadius: 10, color: C.greenBg,
                fontSize: 13, fontWeight: 700, padding: '9px 14px', minHeight: 40, cursor: 'pointer'
              }}>
                {busy === s.id ? '…' : 'Approve'}
              </button>
              <button disabled={busy === s.id} onClick={() => onDecide(s, 'decline')} style={{
                background: 'none', border: `1px solid ${C.redBg}`, borderRadius: 10, color: C.red,
                fontSize: 13, fontWeight: 600, padding: '9px 14px', minHeight: 40, cursor: 'pointer'
              }}>
                Decline
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Weekly movement (Home v2) ──
// A number + a ▲/▼/– delta vs the same metric last week. Same rounded-card
// shell as StatCard, just with the delta line added underneath.
function MovementCard({ label, now, prev }) {
  const delta = now - prev;
  const arrow = delta > 0 ? '▲' : delta < 0 ? '▼' : '–';
  const color = delta > 0 ? C.green : delta < 0 ? C.red : C.faint;
  return (
    <div style={{
      flex: 1, minWidth: 0, background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 14, padding: '14px 12px', textAlign: 'center'
    }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: C.tealSoft }}>{now}</div>
      <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>{label}</div>
      <div style={{ fontSize: 11, fontWeight: 700, color, marginTop: 4 }}>
        {arrow} {Math.abs(delta)} vs last wk
      </div>
    </div>
  );
}

// Tiny inline SVG line over the 6-week net-waiting trend — no chart library.
// `trend` is oldest → newest; the readout compares the two ends.
function Sparkline({ trend }) {
  const w = 260, h = 44, pad = 6;
  const max = Math.max(...trend, 1);
  const min = Math.min(...trend, 0);
  const range = Math.max(max - min, 1);
  const stepX = trend.length > 1 ? (w - pad * 2) / (trend.length - 1) : 0;
  const coords = trend.map((v, i) => [
    pad + i * stepX,
    pad + (1 - (v - min) / range) * (h - pad * 2)
  ]);
  const [lastX, lastY] = coords[coords.length - 1];

  const delta = trend[trend.length - 1] - trend[0];
  const arrow = delta > 0 ? '↑' : delta < 0 ? '↓' : '→';
  const color = delta > 0 ? C.green : delta < 0 ? C.red : C.faint;

  return (
    <div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>Waitlist — last 6 weeks</div>
      <svg width={w} height={h} style={{ display: 'block' }}>
        <polyline points={coords.map(p => p.join(',')).join(' ')} fill="none" stroke={C.teal} strokeWidth="2" />
        <circle cx={lastX} cy={lastY} r="3.5" fill={C.tealSoft} />
      </svg>
      <div style={{ fontSize: 12, color, fontWeight: 600, marginTop: 6 }}>
        Waitlist {Math.abs(delta)} {arrow} over 6 wks
      </div>
    </div>
  );
}

export default function Home({ instructor, onNavigate }) {
  const [stats, setStats]   = useState(null);
  const [slots, setSlots]   = useState([]);
  const [roster, setRoster] = useState(0);
  const [busy, setBusy]     = useState(null);
  const [note, setNote]     = useState(null);
  const isMobile = useIsMobile();
  const isFillMode = instructor.mode === 'fill';

  const load = useCallback(async () => {
    const key = instructor.instructor_key;

    const [oldestRes, slotsRes, rosterCountRes, waitlistAllRes, confirmedSlotsRes, rosterAllRes] = await Promise.all([
      // Unchanged — longest-waiting card.
      supabase.from('waitlist').select('name, area, created_at')
        .eq('instructor_key', key).eq('status', 'waiting')
        .not('name', 'is', null)
        .order('created_at', { ascending: true }).limit(1),
      // Unchanged — live slots feed (ActiveSlots).
      supabase.from('open_slots').select('*')
        .eq('instructor_key', key).in('status', ['open', 'claimed', 'confirmed'])
        .order('created_at', { ascending: false }).limit(15),
      // Unchanged — active roster count (OfferSlotCard audience picker).
      supabase.from('roster').select('id', { count: 'exact', head: true })
        .eq('instructor_key', key).eq('opted_out', false).eq('status', 'active'),
      // New — every waitlist row, once, for all-time counts + weekly deltas
      // + the 6-week trend (replaces 3 separate count queries).
      supabase.from('waitlist').select('status, created_at, offered_at, updated_at')
        .eq('instructor_key', key),
      // New — every confirmed slot, once, for all-time filled + weekly delta
      // + € pro-rating by duration_minutes.
      supabase.from('open_slots').select('status, claimed_at, duration_minutes')
        .eq('instructor_key', key).eq('status', 'confirmed'),
      // New — every non-opted-out roster row, once, for the completed delta.
      supabase.from('roster').select('status, completed_at')
        .eq('instructor_key', key).eq('opted_out', false)
    ]);

    const waitlistRows   = waitlistAllRes.data   || [];
    const confirmedSlots = confirmedSlotsRes.data || [];
    const rosterRows     = rosterAllRes.data     || [];

    const now         = Date.now();
    const DAY         = 86400000;
    const weekAgo     = now - 7 * DAY;
    const twoWeeksAgo = now - 14 * DAY;

    const inWindow = (iso, start, end) => {
      if (!iso) return false;
      const t = new Date(iso).getTime();
      return t >= start && t < end;
    };
    const windowCount = (rows, field, start, end, filter) =>
      rows.filter(r => (!filter || filter(r)) && inWindow(r[field], start, end)).length;

    const wk = {
      enquiries: {
        now:  windowCount(waitlistRows, 'created_at', weekAgo, now),
        prev: windowCount(waitlistRows, 'created_at', twoWeeksAgo, weekAgo)
      },
      accepted: {
        now:  windowCount(waitlistRows, 'offered_at', weekAgo, now, r => r.status === 'accepted'),
        prev: windowCount(waitlistRows, 'offered_at', twoWeeksAgo, weekAgo, r => r.status === 'accepted')
      },
      filled: {
        now:  windowCount(confirmedSlots, 'claimed_at', weekAgo, now),
        prev: windowCount(confirmedSlots, 'claimed_at', twoWeeksAgo, weekAgo)
      },
      completed: {
        now:  windowCount(rosterRows, 'completed_at', weekAgo, now, r => r.status === 'completed'),
        prev: windowCount(rosterRows, 'completed_at', twoWeeksAgo, weekAgo, r => r.status === 'completed')
      }
    };

    // 6-week net-waiting trend, oldest → newest. No history table, so it's
    // reconstructed at each week-end checkpoint as (joined by then) minus
    // (exited by then) — an exit is any row no longer 'waiting', dated by
    // updated_at (the best available proxy: status changes stamp it).
    const trend = [];
    for (let i = 5; i >= 0; i--) {
      const checkpoint = now - i * 7 * DAY;
      const joined = waitlistRows.filter(r => r.created_at && new Date(r.created_at).getTime() <= checkpoint).length;
      const exited = waitlistRows.filter(r => r.status !== 'waiting' && r.updated_at && new Date(r.updated_at).getTime() <= checkpoint).length;
      trend.push(joined - exited);
    }

    // € pro-rating by lesson length: a filled 2-hour slot at the standard
    // hourly rate counts as 2× toward € recovered, not 1×. Null duration
    // (old rows, pre-this-feature) is treated as standard length (1×).
    const standardMinutes = Number(instructor.slot_minutes) || 60;
    const filledValue = confirmedSlots.reduce(
      (sum, s) => sum + (Number(s.duration_minutes) || standardMinutes) / standardMinutes, 0
    );

    setStats({
      waiting:  waitlistRows.filter(r => r.status === 'waiting').length,
      accepted: waitlistRows.filter(r => r.status === 'accepted').length,
      filled:   confirmedSlots.length,
      filledValue,
      oldest:   (oldestRes.data && oldestRes.data[0]) || null,
      wk,
      trend
    });
    // Confirmed slots stay visible ONLY while a deposit is outstanding —
    // that's the state needing the instructor's eyes (Mark received).
    setSlots((slotsRes.data || []).filter(sl =>
      sl.status === 'open' || sl.status === 'claimed' ||
      (sl.status === 'confirmed' && ['pending', 'sent'].includes(sl.deposit_status || ''))
    ));
    setRoster(rosterCountRes.count || 0);
  }, [instructor.instructor_key, instructor.slot_minutes]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    const tick = setInterval(load, 30000);
    return () => {
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
      clearInterval(tick);
    };
  }, [load]);

  async function markDeposit(slot) {
    setBusy(slot.id); setNote(null);
    const res = await apiPost('/deposit-received', { open_slot_id: slot.id });
    setNote(res.ok ? { type: 'ok', text: res.message } : { type: 'err', text: res.error || 'Something went wrong.' });
    setBusy(null);
    await load();
  }

  async function decide(slot, decision) {
    setBusy(slot.id); setNote(null);
    const res = await apiPost('/claim-decision', { open_slot_id: slot.id, decision });
    setNote(res.ok ? { type: 'ok', text: res.message } : { type: 'err', text: res.error || 'Something went wrong.' });
    setBusy(null);
    await load();
  }

  // Multi-instructor schools (e.g. "Vlad & Ksenia") have no single first name
  // to welcome back — drop the name rather than greet only one of them.
  const firstName = (instructor.staff_members || []).length > 1
    ? ''
    : (instructor.instructor_name || '').split(' ')[0];
  const price = Number(instructor.price_per_lesson) || 0;
  // € recovered counts CLAIMED LESSONS only (confirmed slot fills × price),
  // pro-rated by length — a filled 2-hour slot counts double. Accepts are
  // handoffs — they may become twelve lessons or zero — so they get their
  // own honest count and no euro sign.
  const euro = stats && price > 0 ? stats.filledValue * price : null;

  return (
    <div style={{ fontFamily: 'Poppins, sans-serif' }}>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>
        Home
      </div>
      <div style={{ fontSize: 15, color: C.text, marginBottom: 16 }}>
        {firstName ? `Welcome back, ${firstName}.` : 'Welcome back.'}
      </div>

      {!isFillMode && <ShareCard instructor={instructor} isMobile={isMobile} />}

      {note && (
        <div style={{
          marginBottom: 12, padding: '10px 12px', borderRadius: 8, fontSize: 13,
          background: note.type === 'ok' ? C.greenBg : C.redBg,
          color:      note.type === 'ok' ? C.green : C.red
        }}>
          {note.text}
        </div>
      )}

      <OfferSlotCard rosterCount={roster} locations={instructor.locations || []} standardMinutes={instructor.slot_minutes || 60} onSent={load} isMobile={isMobile} isFillMode={isFillMode} />
      <ActiveSlots slots={slots} busy={busy} onDecide={decide} onDeposit={markDeposit} />

      {stats === null ? (
        <div style={{ color: C.muted, fontSize: 13 }}>Loading…</div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
            This week
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            {!isFillMode && <MovementCard label="New enquiries" now={stats.wk.enquiries.now} prev={stats.wk.enquiries.prev} />}
            {!isFillMode && <MovementCard label="Accepted" now={stats.wk.accepted.now} prev={stats.wk.accepted.prev} />}
            <MovementCard label="Slots filled" now={stats.wk.filled.now} prev={stats.wk.filled.prev} />
            <MovementCard label="Completed" now={stats.wk.completed.now} prev={stats.wk.completed.prev} />
          </div>

          {!isFillMode && stats.trend.some(v => v !== 0) && (
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16, marginBottom: 14 }}>
              <Sparkline trend={stats.trend} />
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            {!isFillMode && <StatCard label="Waiting now" value={stats.waiting} />}
            {!isFillMode && <StatCard label="Total accepted" value={stats.accepted} hint="all time" />}
            <StatCard label="Lessons filled" value={stats.filled} hint="all time" />
            {euro !== null && (
              <StatCard label="€ recovered" value={`€${euro.toLocaleString('en-IE')}`} hint="filled slots × your price, by length" />
            )}
          </div>
          {euro === null && stats.filled > 0 && (
            <div style={{ fontSize: 11.5, color: C.faint, marginBottom: 12 }}>
              Set your lesson price in Settings to see what your filled slots are worth in €.
            </div>
          )}

          {!isFillMode && (stats.oldest ? (
            <div style={{
              background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
              padding: 16, display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center', gap: 12
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 2 }}>Longest waiting</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>
                  {stats.oldest.name}
                  {stats.oldest.area ? <span style={{ color: C.muted, fontWeight: 400 }}> · {stats.oldest.area}</span> : null}
                </div>
                <div style={{ fontSize: 12.5, color: C.amber, marginTop: 2 }}>
                  {waitedLabel(stats.oldest.created_at)}
                </div>
              </div>
              <button
                onClick={() => onNavigate && onNavigate('waitlist')}
                style={{
                  background: C.teal, border: 'none', borderRadius: 10,
                  color: C.bg, fontSize: 14, fontWeight: 600,
                  padding: '11px 18px', minHeight: 44, cursor: 'pointer', whiteSpace: 'nowrap'
                }}
              >
                View waitlist →
              </button>
            </div>
          ) : (
            <div style={{
              background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
              padding: 20, color: C.muted, fontSize: 13.5, lineHeight: 1.6
            }}>
              <div style={{ color: C.text, fontWeight: 600, marginBottom: 6 }}>How Visionry Go works</div>
              1. Share your link above — put it in your Facebook bio, Google listing,
              and send it whenever someone asks for lessons.<br />
              2. It asks each person four quick questions and adds them to your list.<br />
              3. When a space opens, hit <b>Accept</b> — or offer a specific slot and
              let the first taker claim it.
            </div>
          ))}
        </>
      )}
    </div>
  );
}
